/** @file Layers one entry's sampled values over the lower tracks (alpha, blend, thresholds). */
import { blendChannel } from '../anim/blendChannel';
import { channelClass } from '../anim/channelClass';
import { POSE_LAYOUT as L } from '../contracts/pose';
import type { Sample } from '../contracts/sample';
import type { TrackEntry } from '../contracts/state';
import type { SkeletonData } from '../skeletonData';

/** Setup value of channel c: the bone's setup for bone channels, 0 for slot and IK channels (they never add). */
function setupOf(data: SkeletonData, c: number, slotStart: number): number {
  if (c >= slotStart) return 0;
  const bone = data.bones[Math.floor(c / L.LOCAL_STRIDE)];
  return bone === undefined ? 0 : (bone.setup[c % L.LOCAL_STRIDE] ?? 0);
}

/** Keyed deform buffers layered by the kernel as additive channels with setup 0 (base 0 when unkeyed). */
function layerDeform(s: Sample, base: Sample, alpha: number, blend: 'replace' | 'add'): void {
  for (let i = 0; i < s.deform.length; i++) {
    const to = s.deform[i];
    if (s.deformKeyed[i] !== 1 || to === undefined) continue;
    const under = base.deformKeyed[i] === 1 ? base.deform[i] : undefined;
    for (let j = 0; j < to.length; j++) to[j] = blendChannel(1, under?.[j] ?? 0, to[j] ?? 0, 0, alpha, blend);
  }
}

/**
 * Rewrites s in place into what this entry contributes over `base` (the lower tracks' result, W2-06): keyed
 * continuous channels and deform through blendChannel; discrete channels (inherit, placeholders, IK flags) pass
 * through when alpha >= attachmentThreshold and draw order when alpha >= drawOrderThreshold, else they are
 * unkeyed (W2-07). Values are absolute (W2-08 R1). The layer's alpha is entry.alpha × `weight`: applyState's
 * fade-in of an upper-track entry with nothing to mix from (m3, R19-01) or a fix layer's share of its base (R19-02).
 */
export function layerEntry(s: Sample, base: Sample, data: SkeletonData, entry: TrackEntry, slotStart: number, ikStart: number, weight: number): void {
  const blend = entry.blend;
  const alpha = entry.alpha * weight;
  const discrete = alpha >= entry.attachmentThreshold;
  for (let c = 0; c < s.mask.length; c++) {
    if ((s.mask[c] ?? 0) === 0) continue;
    const cls = channelClass(c, slotStart, ikStart);
    if (cls === 4) {
      if (!discrete) s.mask[c] = 0;
      continue;
    }
    s.values[c] = blendChannel(cls, base.values[c] ?? 0, s.values[c] ?? 0, setupOf(data, c, slotStart), alpha, blend);
  }
  if (!discrete) {
    s.inherit.fill(-1);
    s.attachments.fill(-2);
  }
  if (alpha < entry.drawOrderThreshold) s.drawOrderKeyed = false;
  layerDeform(s, base, alpha, blend);
}
